import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  IconButton
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../component/Api/instance";
import AttemptReviewModal from "./AttemptReviewModal";

export default function PreviousAttemptsModal({
  open,
  onClose,
  testName = "model_mock",
  paperId,
  catId = 0,
  institutionId = 0,
  paperTitle = "",
}) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const email = localStorage.getItem("userMail");

  useEffect(() => {
    if (open && paperId) {
      fetchAttempts();
    }
  }, [open, paperId, catId, institutionId]);

  const fetchAttempts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post("get/U_GetStudentTestAttempts.php", {
        userId: email,
        testName: testName,
        paperId: paperId,
        catId: catId,
        institutionId: institutionId,
      });

      if (res.data?.status === "success" && Array.isArray(res.data?.attempts)) {
        setAttempts(res.data.attempts);
      } else {
        setAttempts([]);
      }
    } catch (err) {
      console.error("Error fetching previous attempts:", err);
      setError("Failed to load previous attempts.");
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (attemptId) => {
    setSelectedAttemptId(attemptId);
    setReviewOpen(true);
  };

  const handleCloseReview = () => {
    setReviewOpen(false);
    setSelectedAttemptId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            padding: "18px 24px",
          }}
        >
          <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <HistoryIcon style={{ color: "#3b82f6", fontSize: "28px" }} />
            <Box>
              <Typography variant="h6" style={{ fontWeight: 800 }}>
                Previous Attempts & Reviews
              </Typography>
              {paperTitle && (
                <Typography variant="caption" color="textSecondary">
                  {paperTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" style={{ color: "#64748b" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: "20px" }}>
          {loading ? (
            <Box style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "220px", gap: "12px" }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="textSecondary">
                Loading your attempt history...
              </Typography>
            </Box>
          ) : error ? (
            <Typography color="error" style={{ textAlign: "center", padding: "24px" }}>
              {error}
            </Typography>
          ) : attempts.length === 0 ? (
            <Box style={{ textAlign: "center", padding: "40px" }}>
              <Typography color="textSecondary" variant="body1">
                You have not completed any attempts for this exam paper yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} style={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
              <Table>
                <TableHead style={{ backgroundColor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Attempt</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Date & Time</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Correct</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Wrong</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Negative</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Net Score</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((att, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Chip
                          label={`Attempt #${att.attempt_number || attempts.length - index}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          style={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: "0.85rem", color: "#475569" }}>
                        {att.test_date}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#16a34a", fontWeight: 700 }}>
                        {att.correct_count}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#dc2626", fontWeight: 700 }}>
                        {att.incorrect_count}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#dc2626", fontWeight: 600 }}>
                        -{att.negative_marks}
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography style={{ fontWeight: 800, color: "#1d4ed8", fontSize: "0.95rem" }}>
                            {att.final_score} {att.total_questions > 0 ? `/ ${att.total_questions}` : ""}
                          </Typography>
                          {att.percentage > 0 && (
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                              {att.percentage}%
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleOpenReview(att.id)}
                          style={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                          }}
                        >
                          Review Answers
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={onClose} variant="contained" color="inherit" style={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      {reviewOpen && (
        <AttemptReviewModal
          open={reviewOpen}
          onClose={handleCloseReview}
          attemptId={selectedAttemptId}
          paperTitle={paperTitle}
        />
      )}
    </>
  );
}
